import { useEffect, useMemo, useRef, useState } from "react";

import { formatAudioDuration } from "./audio";

interface VoiceMessagePlayerProps {
  audioUrl: string;
  durationSeconds?: number | null;
  label?: string;
  tone?: "orange" | "teal" | "gray";
  compact?: boolean;
}

const TONE_STYLES = {
  orange: {
    shell: "border-[#F3D6CC] bg-gradient-to-r from-[#FFF7F2] to-[#FFFDFC]",
    button: "bg-[#E8521A] text-white shadow-[0_12px_30px_rgba(232,82,26,0.22)] hover:bg-[#d44a16]",
    bar: "bg-[#E8521A]",
    barMuted: "bg-[#F7C9B7]",
    progress: "from-[#E8521A] to-[#F08B66]",
    badge: "bg-white text-[#B64B20] border border-[#F3D6CC]"
  },
  teal: {
    shell: "border-[#CDE7E2] bg-gradient-to-r from-[#F3FBF9] to-[#FCFEFD]",
    button: "bg-[#1A8C7A] text-white shadow-[0_12px_30px_rgba(26,140,122,0.18)] hover:bg-[#157568]",
    bar: "bg-[#1A8C7A]",
    barMuted: "bg-[#B6DDD6]",
    progress: "from-[#1A8C7A] to-[#56B8AA]",
    badge: "bg-white text-[#16695C] border border-[#CDE7E2]"
  },
  gray: {
    shell: "border-gray-200 bg-gradient-to-r from-gray-50 to-white",
    button: "bg-gray-800 text-white shadow-[0_10px_24px_rgba(17,24,39,0.18)] hover:bg-black",
    bar: "bg-gray-700",
    barMuted: "bg-gray-300",
    progress: "from-gray-700 to-gray-500",
    badge: "bg-white text-gray-600 border border-gray-200"
  }
} as const;

const WAVE_BARS = [10, 16, 12, 18, 13, 15, 9, 14];

export default function VoiceMessagePlayer({
  audioUrl,
  durationSeconds,
  label = "Message vocal",
  tone = "orange",
  compact = false
}: VoiceMessagePlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [loadedDuration, setLoadedDuration] = useState(durationSeconds ?? 0);

  const toneStyles = TONE_STYLES[tone];
  const totalDuration = loadedDuration > 0 ? loadedDuration : durationSeconds ?? 0;
  const progress = totalDuration > 0 ? Math.min(100, (currentTime / totalDuration) * 100) : 0;

  const barStyles = useMemo(
    () =>
      WAVE_BARS.map((height, index) => ({
        height,
        animationDelay: `${index * 0.12}s`
      })),
    []
  );

  useEffect(() => {
    setLoadedDuration(durationSeconds ?? 0);
  }, [durationSeconds]);

  useEffect(() => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () => {
      setLoadedDuration(Number.isFinite(audio.duration) ? Math.ceil(audio.duration) : 0);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handlePause = () => {
      if (audio.ended) {
        return;
      }

      setIsPlaying(false);
    };
    const handlePlay = () => {
      setIsPlaying(true);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
    };
  }, []);

  const togglePlayback = async () => {
    const audio = audioRef.current;

    if (!audio) {
      return;
    }

    if (isPlaying) {
      audio.pause();
      return;
    }

    try {
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  };

  return (
    <div
      className={`rounded-[18px] border px-3 py-2.5 shadow-[0_14px_30px_rgba(15,23,42,0.05)] ${toneStyles.shell} ${
        compact ? "max-w-[230px]" : "max-w-[270px]"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={togglePlayback}
          className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${toneStyles.button}`}
          aria-label={isPlaying ? "Mettre en pause le vocal" : "Lire le vocal"}
        >
          <i className={`${isPlaying ? "ri-pause-fill" : "ri-play-fill"} text-base`}></i>
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-[13px] font-semibold text-[#1A1A1A]">{label}</p>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${toneStyles.badge}`}>
              {formatAudioDuration(Math.max(0, Math.ceil(totalDuration || currentTime)))}
            </span>
          </div>

          <div className="relative mt-1.5 overflow-hidden rounded-full bg-white/80 px-2.5 py-1.5 ring-1 ring-white/70">
            <div
              className={`absolute inset-y-0 left-0 rounded-full bg-gradient-to-r opacity-15 ${toneStyles.progress}`}
              style={{ width: `${progress}%` }}
            ></div>
            <div className="relative flex items-end gap-[3px]">
              {barStyles.map((barStyle, index) => (
                <span
                  key={`${audioUrl}-${index}`}
                  className={`block w-[3px] rounded-full ${isPlaying ? `animate-pulse ${toneStyles.bar}` : toneStyles.barMuted}`}
                  style={barStyle}
                ></span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
}
